// Phase 4 · M3 · Medications.
//
// TWO-LAYER MODEL:
//   • medication_authorizations  → parent-owned consent
//   • medication_administrations → teacher-owned append-only log
//
// All read functions tolerate 42P01 so the UI doesn't break before
// the migration is applied.

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

const AUTH_SELECT = `
  id, organization_id, child_id, family_id,
  medication_name, dosage, route, schedule, reason, warnings,
  starts_on, ends_on, active,
  authorized_by, authorized_at, withdrawn_at, withdrawn_by,
  created_at, updated_at
`;

const ADMIN_SELECT = `
  id, organization_id, child_id, authorization_id,
  administered_at, administered_by,
  dose_given, observations,
  medication_name, authorized_dosage, created_at
`;

export const MED_ROUTES = [
  { value: "oral", label: "By mouth" },
  { value: "topical", label: "On skin" },
  { value: "inhaled", label: "Inhaled" },
  { value: "nasal", label: "Nasal" },
  { value: "eye", label: "Eye drops" },
  { value: "ear", label: "Ear drops" },
  { value: "other", label: "Other" },
];

// ---------------- Authorizations ----------------

export async function listAuthorizationsForChild(childId, { activeOnly = true } = {}) {
  if (!isSupabaseConfigured || !childId) return [];
  let q = supabase
    .from("medication_authorizations")
    .select(AUTH_SELECT)
    .eq("child_id", childId)
    .order("active", { ascending: false })
    .order("authorized_at", { ascending: false });
  if (activeOnly) q = q.eq("active", true);
  const { data, error } = await q;
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listAuthorizationsForFamily(familyId) {
  if (!isSupabaseConfigured || !familyId) return [];
  const { data, error } = await supabase
    .from("medication_authorizations")
    .select(`${AUTH_SELECT}, child:children(id, first_name, last_name)`)
    .eq("family_id", familyId)
    .order("active", { ascending: false })
    .order("authorized_at", { ascending: false });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listActiveAuthorizationsForClassroomChildren(childIds) {
  if (!isSupabaseConfigured || !childIds?.length) return [];
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("medication_authorizations")
    .select(`${AUTH_SELECT}, child:children(id, first_name, last_name)`)
    .in("child_id", childIds)
    .eq("active", true)
    .lte("starts_on", today)
    .or(`ends_on.is.null,ends_on.gte.${today}`)
    .order("medication_name", { ascending: true });
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function createAuthorization({
  organizationId,
  childId,
  familyId,
  medicationName,
  dosage,
  route,
  schedule,
  reason,
  warnings,
  startsOn,
  endsOn,
  authorizedBy,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!medicationName?.trim()) throw new Error("Medication name is required");
  if (!dosage?.trim()) throw new Error("Dosage is required");
  const { data, error } = await supabase
    .from("medication_authorizations")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      family_id: familyId,
      medication_name: medicationName.trim(),
      dosage: dosage.trim(),
      route: route || "oral",
      schedule: schedule?.trim() || null,
      reason: reason?.trim() || null,
      warnings: warnings?.trim() || null,
      starts_on: startsOn || new Date().toISOString().slice(0, 10),
      ends_on: endsOn || null,
      authorized_by: authorizedBy,
    })
    .select(AUTH_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function updateAuthorization(id, patch) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  const { data, error } = await supabase
    .from("medication_authorizations")
    .update(patch)
    .eq("id", id)
    .select(AUTH_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function withdrawAuthorization({ id, profileId }) {
  return updateAuthorization(id, {
    active: false,
    withdrawn_at: new Date().toISOString(),
    withdrawn_by: profileId,
  });
}

// ---------------- Administrations (log) ----------------

export async function logAdministration({
  organizationId,
  childId,
  authorization,             // full auth row, used for snapshot fields
  administeredAt,
  administeredBy,
  doseGiven,
  observations,
}) {
  if (!isSupabaseConfigured) throw new Error("Requires live Supabase");
  if (!authorization?.id) throw new Error("Authorization required");
  if (!doseGiven?.trim()) throw new Error("Dose given is required");
  const { data, error } = await supabase
    .from("medication_administrations")
    .insert({
      organization_id: organizationId,
      child_id: childId,
      authorization_id: authorization.id,
      administered_at: administeredAt || new Date().toISOString(),
      administered_by: administeredBy,
      dose_given: doseGiven.trim(),
      observations: observations?.trim() || null,
      medication_name: authorization.medication_name,
      authorized_dosage: authorization.dosage,
    })
    .select(ADMIN_SELECT)
    .single();
  if (error) throw error;
  return data;
}

export async function listAdministrationsForChild(childId, { limit = 25 } = {}) {
  if (!isSupabaseConfigured || !childId) return [];
  const { data, error } = await supabase
    .from("medication_administrations")
    .select(`${ADMIN_SELECT},
      administrator:profiles!medication_administrations_administered_by_fkey(id, full_name, email)
    `)
    .eq("child_id", childId)
    .order("administered_at", { ascending: false })
    .limit(limit);
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

export async function listAdministrationsForAuthorization(authorizationId, { limit = 50 } = {}) {
  if (!isSupabaseConfigured || !authorizationId) return [];
  const { data, error } = await supabase
    .from("medication_administrations")
    .select(`${ADMIN_SELECT},
      administrator:profiles!medication_administrations_administered_by_fkey(id, full_name)
    `)
    .eq("authorization_id", authorizationId)
    .order("administered_at", { ascending: false })
    .limit(limit);
  if (error) {
    if (error.code === "42P01") return [];
    throw error;
  }
  return data || [];
}

// ---------------- helpers ----------------

export function isAuthorizationActiveToday(auth) {
  if (!auth?.active) return false;
  const today = new Date().toISOString().slice(0, 10);
  if (auth.starts_on && auth.starts_on > today) return false;
  if (auth.ends_on && auth.ends_on < today) return false;
  return true;
}

export function formatAdministeredAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function routeLabel(value) {
  return MED_ROUTES.find((r) => r.value === value)?.label || value || "—";
}
